import { z } from 'zod';

/**
 * Environment variables shared by every app. Each app extends this schema with
 * its own variables and calls `loadConfig` at startup: if anything is missing or
 * invalid, the process refuses to start.
 */

/** Explicit "true"/"false"; `z.coerce.boolean` would turn "false" into true. */
export const envBoolean = z.enum(['true', 'false']).transform((v) => v === 'true');

export const baseEnvSchema = z.object({
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).default('info'),
  DATABASE_URL: z.url({ protocol: /^postgres(ql)?$/ }),
  /**
   * ISO date-time the whole app takes as "now" (e.g. 2026-03-31T18:00).
   * Without an offset it is read as Seville local time. Forbidden in production.
   */
  SIMULATED_CLOCK: z.iso.datetime({ local: true, offset: true }).or(z.iso.date()).optional(),
});

export type BaseConfig = z.infer<typeof baseEnvSchema>;

export class ConfigError extends Error {
  override name = 'ConfigError';
}

export function loadConfig<T extends z.ZodType<BaseConfig>>(
  schema: T,
  env: NodeJS.ProcessEnv = process.env,
): z.infer<T> {
  // Empty values in .env count as unset
  const defined = Object.fromEntries(Object.entries(env).filter(([, v]) => v !== ''));
  const result = schema.safeParse(defined);
  if (!result.success) {
    const details = result.error.issues
      .map((i) => `  - ${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('\n');
    throw new ConfigError(`Invalid configuration:\n${details}`);
  }
  const config = result.data;
  if (config.NODE_ENV === 'production' && config.SIMULATED_CLOCK) {
    throw new ConfigError('SIMULATED_CLOCK cannot be used in production');
  }
  return config;
}
